/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
// TODO: remove this comment, it's only needed for scroll buttons, it`s a palliative solution
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useRef, useState } from 'react'
import type { StyleProp } from '@vtex/admin-ui'
import { Text, IconArrowLeft, IconArrowRight, csx } from '@vtex/admin-ui'
import { FormattedMessage } from 'react-intl'
import { last } from 'ramda'

import { OutOfStoreListItem } from './OutOfStoreListItem'
import type { DragData } from '../../shared/types'
import { List } from '../../shared/types'
import { useMovementsModal } from '../../hooks/useMovementsModal'
import { useOutOfStoreList } from '../../hooks/useOutOfStoreList'
import type { ListsItem } from '../../context/TurnListProvider'
import { usePositionsLists } from '../../states'
import { useDropWithHighlights } from '../../hooks/useDropWithHighlights'
import { useShowArrows } from '../../hooks/useShowArrows'

const containerStyles: StyleProp = {
  order: 3,
  position: 'relative',
  flexDirection: 'column',
  gridColumn: '1/3',
  borderTop: '#E0E2E7',
  background: '$white',
  border: '$critical',
  height: 'full',
  width: 'full',
  display: 'flex',
  alignContent: 'center',
  justifyContent: 'center',
  padding: '$space-4',
}

const outOfStoreListStyles: StyleProp = {
  overflowX: 'auto',
  marginTop: '$space-2',
  minHeight: '124px',
}

const arrowStyles: StyleProp = {
  height: 'fit-content',
  display: 'flex',
  borderRadius: '50%',
  padding: '$space-2',
  color: '$blue40',
  border: '$neutral',
  backgroundColor: '$white',
}

const arrowContainerStyles: StyleProp = {
  position: 'absolute',
  bottom: '35%',
  zIndex: 1,
}

export const OutOfStoreList = () => {
  const { outOfStorePositions } = usePositionsLists()
  const { moveWaitingListItemToOutOfStore } = useOutOfStoreList()
  const { open } = useMovementsModal()
  const itemsContainerRef = useRef<HTMLDivElement>(null)
  const firstElementRef = useRef<HTMLDivElement>(null)
  const lastElementRef = useRef<HTMLDivElement>(null)
  const [showLeftNavigation, setShowLeftNavigation] = useState(false)
  const [showRightNavigation, setShowRightNavigation] = useState(false)

  const [highlightStyles, drop] = useDropWithHighlights<
    DragData<ListsItem['waiting']>,
    unknown,
    unknown
  >(() => ({
    accept: List.Waiting,
    drop: ({ queueItem }) => {
      // circular reference, same types but distinct objects
      const nextSteps = ((last(queueItem.unfinishedMovements)?.nextSteps ??
        []) as unknown) as typeof queueItem.unfinishedMovements

      open({
        movements: nextSteps,
        salesAssociate: queueItem.salesAssociate,
        onConfirm: (selectedMovementId) => {
          const selectedMovement = nextSteps.find(
            (nextStep) => nextStep.id === selectedMovementId
          )

          if (!selectedMovement) return

          moveWaitingListItemToOutOfStore(queueItem, selectedMovement)
        },
      })
    },
  }))

  const outOfStoresCounter = outOfStorePositions.length ?? 0

  useShowArrows({
    elementRef: firstElementRef,
    setShowNavigation: setShowLeftNavigation,
    sellers: outOfStorePositions.length,
  })

  useShowArrows({
    elementRef: lastElementRef,
    setShowNavigation: setShowRightNavigation,
    sellers: outOfStorePositions.length,
  })

  const leaveOnlyTopBorderStyles: StyleProp = {
    borderBottom: 'none',
    borderLeft: 'none',
    borderRight: 'none',
  }

  return (
    <div
      className={csx({
        ...containerStyles,
        ...highlightStyles,
        ...leaveOnlyTopBorderStyles,
      })}
      ref={drop}
      data-testid="out-of-store-list"
    >
      <Text variant="title1">
        <FormattedMessage
          id="outOfStoreTitle"
          values={{ outOfStoresCounter }}
        />
      </Text>

      <div
        className={csx({
          ...arrowContainerStyles,
          left: 0,
          background:
            'linear-gradient(to left, rgba(255, 255, 255, 0) 0%, white 100%) !important',
          display: showLeftNavigation ? 'flex' : 'none',
        })}
      >
        <div
          onClick={() => {
            itemsContainerRef.current!.scroll({
              left:
                itemsContainerRef.current!.scrollLeft -
                itemsContainerRef.current!.offsetWidth,
              behavior: 'smooth',
            })
          }}
          className={csx({ ...arrowStyles })}
        >
          <IconArrowLeft />
        </div>
      </div>
      <div
        ref={itemsContainerRef}
        className={csx({
          ...outOfStoreListStyles,
          display: 'flex',
        })}
      >
        {outOfStorePositions.map((outOfStoreItem, index) => {
          const ref =
            index === 0
              ? firstElementRef
              : index === outOfStorePositions.length - 1
              ? lastElementRef
              : null

          return (
            <OutOfStoreListItem
              ref={ref}
              key={outOfStoreItem.salesAssociate.id}
              index={index}
              outOfStoreItem={outOfStoreItem}
            />
          )
        })}
      </div>
      <div
        className={csx({
          ...arrowContainerStyles,
          right: 0,
          background:
            'linear-gradient(to right, rgba(255, 255, 255, 0) 0%, white 100%)',
          display: showRightNavigation ? 'flex' : 'none',
        })}
      >
        <div
          onClick={() => {
            itemsContainerRef.current!.scroll({
              left:
                itemsContainerRef.current!.scrollLeft +
                itemsContainerRef.current!.offsetWidth,
              behavior: 'smooth',
            })
          }}
          className={csx({ ...arrowStyles })}
        >
          <IconArrowRight />
        </div>
      </div>
    </div>
  )
}
